import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.js';
import { oidcEnabled, startSsoLogin } from '../auth/oidc.js';
import { takeSsoError } from '../auth/ssoBridge.js';
import { errorMessage } from '../api/client.js';

export function LoginPage() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const redirect = params.get('redirect') || '/';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Surface why a completed SSO sign-in was rejected (e.g. not provisioned).
  useEffect(() => {
    const ssoError = takeSsoError();
    if (ssoError) setError(ssoError);
  }, []);

  // Already signed in (or hydration just finished) — go where the user intended.
  useEffect(() => {
    if (!loading && user) navigate(redirect, { replace: true });
  }, [loading, user, navigate, redirect]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await login(email.trim(), password);
      navigate(redirect, { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-card__brand">
          <span className="sidebar__logo">TT</span>
          <h1>TipsTrack</h1>
        </div>
        <p className="login-card__subtitle">SDR hiring, synced with Indeed.</p>

        {error && <div className="alert alert--error">{error}</div>}

        <form onSubmit={onSubmit} className="login-form">
          <label className="field">
            <span className="field__label">Email</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </label>
          <label className="field">
            <span className="field__label">Password</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </label>
          <button type="submit" className="btn btn--primary btn--block" disabled={submitting}>
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        {oidcEnabled && (
          <>
            <div className="login-divider">
              <span>or</span>
            </div>
            <button
              type="button"
              className="btn btn--secondary btn--block"
              onClick={() => void startSsoLogin(redirect)}
            >
              Sign in with TIPSSSO
            </button>
          </>
        )}
      </div>
    </div>
  );
}
